import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, ArrowLeft, Sparkles, Target, FileText, 
  Search, MessageSquare, Users, Check, X, Compass,
  HelpCircle, ChevronRight, GraduationCap, Award, BookOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { authenticatedFetch } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * GPS Onboarding Component
 * Personalized onboarding flow for CareerSupport.io
 * 
 * Features:
 * - 3-screen flow: Welcome → Intro → 6 Questions
 * - Stores responses in backend user profile
 * - Calculates user intent and recommendations
 * - Non-blocking, can be skipped anytime
 */

// Question options configuration
const QUESTIONS = [
  {
    id: 'current_situation',
    question: 'What best describes your current situation?',
    options: [
      { value: 'unemployed', label: 'Unemployed', icon: Search },
      { value: 'employed_stuck', label: 'Employed but stuck', icon: Target },
      { value: 'career_changer', label: 'Career changer', icon: Compass },
      { value: 'returning', label: 'Returning to work', icon: ArrowRight }
    ]
  },
  {
    id: 'biggest_challenge',
    question: "What's your biggest challenge right now?",
    options: [
      { value: 'resume', label: 'Resume', icon: FileText },
      { value: 'finding_jobs', label: 'Finding the right jobs', icon: Search },
      { value: 'interviews', label: 'Interviews', icon: MessageSquare },
      { value: 'confidence', label: 'Confidence or clarity', icon: Target }
    ]
  },
  {
    id: 'knows_job_type',
    question: 'Do you know what type of job you want?',
    options: [
      { value: 'yes', label: 'Yes', icon: Check },
      { value: 'somewhat', label: 'Somewhat', icon: HelpCircle },
      { value: 'not_yet', label: 'Not yet', icon: Compass }
    ]
  },
  {
    id: 'support_preference',
    question: 'How do you prefer support?',
    options: [
      { value: 'step_by_step', label: 'Step-by-step guidance', icon: Target },
      { value: 'self_paced', label: 'Self-paced tools', icon: Sparkles }
    ]
  },
  {
    id: 'coaching_interest',
    question: 'Would you like optional coaching support later?',
    options: [
      { value: 'yes', label: 'Yes', icon: Check },
      { value: 'maybe', label: 'Maybe', icon: HelpCircle },
      { value: 'not_now', label: 'Not right now', icon: X }
    ]
  },
  {
    id: 'education_level',
    question: "What's your highest level of education?",
    options: [
      { value: 'no_formal', label: 'No Formal Education', icon: BookOpen },
      { value: 'some_high_school', label: 'Some High School', icon: BookOpen },
      { value: 'high_school_diploma', label: 'High School Diploma/GED', icon: GraduationCap },
      { value: 'some_college', label: 'Some College', icon: GraduationCap },
      { value: 'associate', label: "Associate's Degree", icon: Award },
      { value: 'bachelor', label: "Bachelor's Degree", icon: Award },
      { value: 'master', label: "Master's Degree", icon: Award },
      { value: 'doctoral', label: 'Doctoral Degree', icon: Award }
    ]
  }
];

const GPSOnboarding = ({ onComplete, onSkip, isRetake = false }) => {
  const navigate = useNavigate();
  const [screen, setScreen] = useState(isRetake ? 1 : 0); // 0: Welcome, 1: Intro, 2: Questions
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Check if all questions are answered
  const allQuestionsAnswered = QUESTIONS.every(q => answers[q.id]);

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    
    // Auto-advance to next question after a brief delay
    if (currentQuestion < QUESTIONS.length - 1) {
      setTimeout(() => {
        setCurrentQuestion(prev => prev + 1);
      }, 300);
    }
  };

  const handleSubmit = async () => {
    if (!allQuestionsAnswered) return;
    
    setIsSubmitting(true);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/gps/responses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(answers)
      });
      
      if (response.ok) {
        const data = await response.json();
        setResult(data);
        
        // Move to result screen (could be screen 3)
        if (onComplete) {
          onComplete(data);
        }
      }
    } catch (error) {
      console.error('Failed to save GPS responses:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    // Store skip in localStorage for persistence across sessions
    localStorage.setItem('gps_onboarding_skipped', 'true');
    if (onSkip) {
      onSkip();
    }
  };

  // Screen 0: Welcome
  if (screen === 0) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
          {/* Hero Section */}
          <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 p-8 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Compass className="w-8 h-8" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-3">
              Start Where You Are.
            </h1>
            <h2 className="text-xl sm:text-2xl font-semibold mb-4 text-blue-100">
              We&apos;ll Help You Move Forward.
            </h2>
          </div>
          
          {/* Content */}
          <div className="p-6 sm:p-8">
            <p className="text-gray-600 text-center text-lg mb-8">
              CareerSupport.io gives you guided tools and support to overcome career barriers without overwhelm.
            </p>
            
            <div className="space-y-3">
              <Button 
                onClick={() => setScreen(1)}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-lg py-6"
              >
                Get Started (Free)
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              
              <button 
                onClick={handleSkip}
                className="w-full text-gray-500 hover:text-gray-700 text-sm py-2"
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Screen 1: GPS Intro
  if (screen === 1) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-6 text-white">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Target className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Let&apos;s Personalize Your Journey</h2>
                <p className="text-indigo-100 text-sm">6 quick questions</p>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6 sm:p-8">
            <p className="text-gray-600 text-center text-lg mb-6">
              Answer a few quick questions so we can guide you to the tools that matter most right now.
            </p>
            
            {/* What to expect */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <h3 className="font-medium text-gray-900 mb-2">What to expect:</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Takes less than 2 minutes
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Get personalized recommendations
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  You can change answers anytime
                </li>
              </ul>
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={() => setScreen(0)}
                className="flex-1"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button 
                onClick={() => setScreen(2)}
                className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              >
                Start Questions
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            <button 
              onClick={handleSkip}
              className="w-full text-gray-500 hover:text-gray-700 text-sm py-2 mt-3"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Screen 2: Questions
  const question = QUESTIONS[currentQuestion];
  const progress = ((currentQuestion + 1) / QUESTIONS.length) * 100;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl animate-in fade-in duration-200">
        {/* Progress Bar */}
        <div className="h-2 bg-gray-100">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Question {currentQuestion + 1} of {QUESTIONS.length}</span>
          </div>
          <button 
            onClick={handleSkip}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Question */}
        <div className="p-6 sm:p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 text-center">
            {question.question}
          </h2>
          
          {/* Options */}
          <div className="space-y-3">
            {question.options.map((option) => {
              const Icon = option.icon;
              const isSelected = answers[question.id] === option.value;
              
              return (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(question.id, option.value)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${
                    isSelected 
                      ? 'border-blue-500 bg-blue-50 text-blue-700' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isSelected ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'
                  }`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="font-medium">{option.label}</span>
                  {isSelected && (
                    <Check className="w-5 h-5 ml-auto text-blue-500" />
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Navigation */}
          <div className="flex gap-3 mt-8">
            {currentQuestion > 0 && (
              <Button 
                variant="outline"
                onClick={() => setCurrentQuestion(prev => prev - 1)}
                className="flex-1"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            )}
            
            {currentQuestion < QUESTIONS.length - 1 ? (
              <Button 
                onClick={() => setCurrentQuestion(prev => prev + 1)}
                disabled={!answers[question.id]}
                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={!allQuestionsAnswered || isSubmitting}
                className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600"
              >
                {isSubmitting ? 'Saving...' : 'See My Recommendations'}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Hook to check if GPS onboarding should be shown
export const useGPSOnboarding = () => {
  const [shouldShow, setShouldShow] = useState(false);
  const [gpsData, setGpsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkGPSStatus();
  }, []);

  const checkGPSStatus = async () => {
    try {
      // First check localStorage for skip (persists across sessions)
      if (localStorage.getItem('gps_onboarding_skipped') === 'true') {
        setShouldShow(false);
        setLoading(false);
        return;
      }

      const response = await authenticatedFetch(`${API_URL}/api/gps/status`);
      if (response.ok) {
        const data = await response.json();
        setGpsData(data);
        setShouldShow(data.should_show_onboarding);
      }
    } catch (error) {
      console.debug('GPS status check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetOnboarding = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/api/gps/reset`, {
        method: 'POST'
      });
      if (response.ok) {
        localStorage.removeItem('gps_onboarding_skipped');
        setShouldShow(true);
        setGpsData(null);
      }
    } catch (error) {
      console.error('Failed to reset GPS onboarding:', error);
    }
  };

  return { shouldShow, setShouldShow, gpsData, loading, resetOnboarding, checkGPSStatus };
};

export default GPSOnboarding;
